<template>
  <v-card class="export-card">
    <v-card-title class="d-flex align-center bg-primary-lighten-5">
      <v-icon color="primary" class="mr-2">mdi-file-export</v-icon>
      Enhanced Exports
    </v-card-title>

    <v-card-text class="pt-4">
      <v-form v-model="valid">
        <!-- Date Range Selection -->
        <v-row>
          <v-col cols="12" sm="6">
            <v-menu
              v-model="datepicker1"
              :close-on-content-click="false"
              min-width="auto"
            >
              <template #activator="{ props }">
                <v-text-field
                  v-model="formattedFromDate"
                  label="Exporteren vanaf"
                  prepend-inner-icon="mdi-calendar"
                  variant="outlined"
                  readonly
                  :rules="[rules.required]"
                  v-bind="props"
                  density="comfortable"
                  class="date-field"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="dates.from_date"
                @update:model-value="datepicker1 = false"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" sm="6">
            <v-menu
              v-model="datepicker2"
              :close-on-content-click="false"
              min-width="auto"
            >
              <template #activator="{ props }">
                <v-text-field
                  v-model="formattedToDate"
                  label="Exporteren tot"
                  prepend-inner-icon="mdi-calendar"
                  variant="outlined"
                  readonly
                  :rules="[rules.required]"
                  v-bind="props"
                  density="comfortable"
                  class="date-field"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="dates.to_date"
                @update:model-value="datepicker2 = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Export Options with Fold-out Menus -->
        <div class="export-options">
          <!-- DG Export -->
          <v-card class="mb-4" variant="outlined">
            <v-card-title class="d-flex align-center justify-space-between">
              <div class="d-flex align-center">
                <v-icon color="success" class="mr-2"
                  >mdi-microsoft-excel</v-icon
                >
                DG Overzicht
              </div>
              <v-btn
                color="success"
                :disabled="!valid"
                :loading="dgLoading"
                size="large"
                @click="generateDG"
              >
                Export
              </v-btn>
            </v-card-title>
            <v-card-subtitle>
              Excel export van accounts en rapportage statistieken
            </v-card-subtitle>
          </v-card>

          <!-- Reports Export with Filters -->
          <v-card variant="outlined">
            <v-card-title class="d-flex align-center justify-space-between">
              <div class="d-flex align-center">
                <v-icon color="primary" class="mr-2">mdi-file-document</v-icon>
                Verslagen Export
              </div>
              <div class="d-flex align-center gap-2">
                <v-btn
                  variant="text"
                  size="small"
                  @click="reportsExpanded = !reportsExpanded"
                >
                  <v-icon>{{
                    reportsExpanded ? "mdi-chevron-up" : "mdi-chevron-down"
                  }}</v-icon>
                  {{ reportsExpanded ? "Minder opties" : "Meer opties" }}
                </v-btn>
                <v-btn
                  color="primary"
                  :disabled="!valid"
                  :loading="reportsLoading"
                  size="large"
                  @click="generateReports"
                >
                  Export
                </v-btn>
              </div>
            </v-card-title>
            <v-card-subtitle>
              HTML export van dagverslagen met filtering opties
            </v-card-subtitle>

            <!-- Expandable Filter Options -->
            <v-expand-transition>
              <div v-show="reportsExpanded">
                <v-divider></v-divider>
                <v-card-text>
                  <v-row>
                    <!-- Day Report Type Filter -->
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="reportFilters.day_report_type_ids"
                        :items="dayReportTypeOptions"
                        item-title="name"
                        item-value="id"
                        label="Dagverslag types"
                        multiple
                        chips
                        variant="outlined"
                        density="comfortable"
                        prepend-inner-icon="mdi-calendar-text"
                        hint="Laat leeg voor alle types"
                        persistent-hint
                        clearable
                      ></v-select>
                    </v-col>

                    <!-- Day Report Line Type Filter -->
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="reportFilters.day_report_line_types"
                        :items="dayReportLineTypeOptions"
                        label="Activiteit types"
                        multiple
                        chips
                        variant="outlined"
                        density="comfortable"
                        prepend-inner-icon="mdi-format-list-bulleted"
                        hint="Laat leeg voor alle activiteiten"
                        persistent-hint
                        clearable
                      ></v-select>
                    </v-col>

                    <!-- Important Only Filter -->
                    <v-col cols="12">
                      <v-switch
                        v-model="reportFilters.only_important"
                        label="Alleen belangrijke activiteiten"
                        color="primary"
                        density="comfortable"
                        hide-details
                      ></v-switch>
                    </v-col>
                  </v-row>

                  <!-- Filter Summary -->
                  <div v-if="hasActiveFilters" class="mt-4">
                    <v-divider class="mb-3"></v-divider>
                    <div class="text-subtitle-2 mb-2">Actieve filters:</div>
                    <div class="d-flex flex-wrap gap-2">
                      <v-chip
                        v-if="reportFilters.day_report_type_ids.length"
                        size="small"
                        color="primary"
                        variant="tonal"
                      >
                        {{ reportFilters.day_report_type_ids.length }}
                        dagverslag type(s)
                      </v-chip>
                      <v-chip
                        v-if="reportFilters.day_report_line_types.length"
                        size="small"
                        color="secondary"
                        variant="tonal"
                      >
                        {{ reportFilters.day_report_line_types.length }}
                        activiteit type(s)
                      </v-chip>
                      <v-chip
                        v-if="reportFilters.only_important"
                        size="small"
                        color="warning"
                        variant="tonal"
                      >
                        Alleen belangrijke
                      </v-chip>
                      <v-btn
                        variant="text"
                        size="small"
                        color="error"
                        @click="clearFilters"
                      >
                        Wis filters
                      </v-btn>
                    </div>
                  </div>
                </v-card-text>
              </div>
            </v-expand-transition>
          </v-card>
        </div>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { useAlertStore } from "@/stores/alertStore";
import { useDayReportTypesStoreRefactor } from "@/stores/dayReportTypesStoreRefactor";
import axios from "axios";
import moment from "moment";

// Store instances
const alertStore = useAlertStore();
const dayReportTypesStore = useDayReportTypesStoreRefactor();

// Reactive state
const valid = ref(false);
const datepicker1 = ref(false);
const datepicker2 = ref(false);
const dgLoading = ref(false);
const reportsLoading = ref(false);
const reportsExpanded = ref(false);

const dates = reactive({
  from_date: moment().format("YYYY-MM-DD"),
  to_date: moment().format("YYYY-MM-DD"),
});

const reportFilters = reactive({
  day_report_type_ids: [],
  day_report_line_types: [],
  only_important: false,
});

// Computed properties for formatted dates
const formattedFromDate = computed(() => {
  return dates.from_date ? moment(dates.from_date).format("DD-MM-YYYY") : "";
});

const formattedToDate = computed(() => {
  return dates.to_date ? moment(dates.to_date).format("DD-MM-YYYY") : "";
});

// Filter options
const dayReportTypeOptions = computed(() => {
  return dayReportTypesStore.dayReportTypes || [];
});

const dayReportLineTypeOptions = [
  { title: "Administratie", value: "administration" },
  { title: "Netwerken", value: "networking" },
  { title: "Veldwerk", value: "fieldwork" },
  { title: "Casussen", value: "cases" },
  { title: "Overig", value: "other" },
];

// Check if any filters are active
const hasActiveFilters = computed(() => {
  return (
    reportFilters.day_report_type_ids.length > 0 ||
    reportFilters.day_report_line_types.length > 0 ||
    reportFilters.only_important
  );
});

// Validation rules
const rules = {
  required: (value) => !!value || "Dit veld is verplicht.",
};

// Methods
function clearFilters() {
  reportFilters.day_report_type_ids = [];
  reportFilters.day_report_line_types = [];
  reportFilters.only_important = false;
}

async function generateDG() {
  if (!valid.value) return false;

  dgLoading.value = true;
  try {
    const exportData = {
      export: {
        from_date: moment(dates.from_date).format("YYYY-MM-DD"),
        to_date: moment(dates.to_date).format("YYYY-MM-DD"),
      },
    };

    const response = await axios.post("/api/v1/exports/dg", exportData, {
      headers: {
        "Content-Type": "application/json",
      },
      transformRequest: [
        (data, headers) => {
          headers.Accept = "text/csv";
          return JSON.stringify(data);
        },
      ],
      responseType: "blob",
    });

    handleFile(response.data, "dg.csv");
    alertStore.setAlert("DG export succesvol gegenereerd", "success");
  } catch (error) {
    console.error("Error generating DG export:", error);
    alertStore.setAlert(
      "Er is een fout opgetreden bij het genereren van de DG export",
      "error",
    );
  } finally {
    dgLoading.value = false;
  }
}

async function generateReports() {
  if (!valid.value) return false;

  reportsLoading.value = true;
  try {
    const exportData = {
      export: {
        from_date: moment(dates.from_date).format("YYYY-MM-DD"),
        to_date: moment(dates.to_date).format("YYYY-MM-DD"),
        day_report_type_ids: reportFilters.day_report_type_ids,
        day_report_line_types: reportFilters.day_report_line_types,
        only_important: reportFilters.only_important,
      },
    };

    const response = await axios.post("/api/v1/exports/reports", exportData, {
      headers: {
        "Content-Type": "application/json",
      },
      transformRequest: [
        (data, headers) => {
          headers.Accept = "text/html";
          return JSON.stringify(data);
        },
      ],
      responseType: "blob",
    });

    handleFile(response.data, "reports.html");
    alertStore.setAlert("Verslagen export succesvol gegenereerd", "success");
  } catch (error) {
    console.error("Error generating reports export:", error);
    alertStore.setAlert(
      "Er is een fout opgetreden bij het genereren van de verslagen export",
      "error",
    );
  } finally {
    reportsLoading.value = false;
  }
}

function handleFile(data, file_name) {
  const fileURL = window.URL.createObjectURL(new Blob([data]));
  const fileLink = document.createElement("a");

  fileLink.href = fileURL;
  fileLink.setAttribute("download", file_name);
  document.body.appendChild(fileLink);

  fileLink.click();

  // Clean up
  setTimeout(() => {
    document.body.removeChild(fileLink);
    window.URL.revokeObjectURL(fileURL);
  }, 100);
}

// Load data on mount
onMounted(async () => {
  try {
    await dayReportTypesStore.fetchAll();
  } catch (error) {
    console.error("Error loading day report types:", error);
  }
});
</script>

<style scoped>
.export-card {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.export-card .v-card-title {
  font-size: 1.25rem;
  padding: 16px 20px;
}

.export-card .v-card-text {
  padding: 20px;
}

.date-field {
  margin-bottom: 8px;
}

.export-options .v-card {
  transition: all 0.2s ease;
}

.export-options .v-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.gap-2 {
  gap: 8px;
}

.gap-3 {
  gap: 12px;
}

@media (max-width: 600px) {
  .export-card .v-card-title {
    padding: 12px 16px;
    font-size: 1.1rem;
  }

  .export-card .v-card-text {
    padding: 16px;
  }
}
</style>
